import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, ReferenceArea } from 'recharts';
import { FileText, Calendar, DollarSign, TrendingUp, ChevronDown, ChevronRight, Building2, RefreshCw, Lock, AlertCircle } from 'lucide-react';

const VENDOR_BILLS_QUERY = `
  SELECT t.[id], t.[tranid], t.[trandate], t.[entity], t.[foreigntotal], t.[status],
         v.[companyname], v.[entityid], a.[acctnumber], a.[fullname]
  FROM [OF-NetSuite].[SuiteQL].[transaction] t
  INNER JOIN [OF-NetSuite].[SuiteQL].[vendor] v ON t.[entity] = v.[id]
  LEFT JOIN [OF-NetSuite].[SuiteQL].[account] a ON v.[expenseaccount] = a.[id]
  WHERE t.[type] = 'VendBill' AND t.[trandate] >= '${new Date().getFullYear()}-01-01'
  ORDER BY t.[trandate] ASC
`;

async function fetchFromCData(sql) {
  const response = await fetch('/api/query-netsuite', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ query: sql }),
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  if (data.error) {
    throw new Error(data.error.message || 'Query failed');
  }

  return data;
}

function toProperCase(str) {
  if (!str) return str;
  const lowerWords = ['and', 'of', 'the', 'in', 'for', 'to', 'a', 'an'];
  const upperWords = ['LLC', 'LTD', 'INC', 'CO', 'CORP', 'LP', 'LLP', 'USA', 'US', 'MA'];
  return str.split(' ').map((word, i) => {
    const upper = word.replace(/[^A-Za-z]/g, '').toUpperCase();
    if (upperWords.includes(upper)) return word.toUpperCase();
    if (i > 0 && lowerWords.includes(word.toLowerCase())) return word.toLowerCase();
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(' ');
}

function transformBills(cdataResponse) {
  const result = cdataResponse.results?.[0];
  if (!result?.rows?.length) return [];

  return result.rows.map(row => {
    const acctnumber = row[8];
    const fullname = row[9];
    let glAccount = 'N/A';
    if (acctnumber && fullname) {
      glAccount = `${acctnumber} - ${fullname}`;
    } else if (fullname) {
      glAccount = fullname;
    }

    return {
      id: row[0],
      tranid: row[1] || '',
      trandate: row[2] || '',
      vendorId: row[3],
      amount: Math.abs(parseFloat(row[4]) || 0),
      status: row[5] || '',
      vendor: toProperCase(row[6]) || 'Unknown',
      entityId: row[7] || '',
      glAccount,
    };
  });
}

function computeTop10(bills) {
  const vendorMap = {};
  bills.forEach(bill => {
    if (!vendorMap[bill.vendorId]) {
      vendorMap[bill.vendorId] = {
        vendorId: bill.vendorId,
        name: bill.vendor,
        entityId: bill.entityId,
        billCount: 0,
        ytdTotal: 0,
      };
    }
    vendorMap[bill.vendorId].billCount++;
    vendorMap[bill.vendorId].ytdTotal += bill.amount;
  });

  const sorted = Object.values(vendorMap)
    .sort((a, b) => b.ytdTotal - a.ytdTotal)
    .slice(0, 10);

  return sorted.map((v, i) => ({ ...v, rank: i + 1 }));
}

const statusLabels = {
  "A": { label: "Open", color: "#ef4444" },
  "B": { label: "Paid", color: "#22c55e" },
  "V": { label: "Voided", color: "#6b7280" }
};

const PASSWORD_HASH = 'OFVendors2026!';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => sessionStorage.getItem('of-auth') === 'true');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState(false);
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [activeTab, setActiveTab] = useState('weekly');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [bills, setBills] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setFetchError(null);
      const response = await fetchFromCData(VENDOR_BILLS_QUERY);
      const transformed = transformBills(response);
      setBills(transformed);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('Failed to fetch data from NetSuite:', err);
      setFetchError(err.message);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      setIsLoading(true);
      fetchData().finally(() => setIsLoading(false));
    }
  }, [isAuthenticated, fetchData]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === PASSWORD_HASH) {
      sessionStorage.setItem('of-auth', 'true');
      setIsAuthenticated(true);
      setAuthError(false);
    } else {
      setAuthError(true);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  const top10Vendors = useMemo(() => computeTop10(bills), [bills]);

  const weeklySummary = useMemo(() => {
    const weeks = {};

    bills.forEach(bill => {
      const date = new Date(bill.trandate);
      const year = date.getFullYear();
      const startOfYear = new Date(year, 0, 1);
      const days = Math.floor((date - startOfYear) / 86400000);
      const weekNum = Math.ceil((days + startOfYear.getDay() + 1) / 7);
      const weekKey = `${year}-W${weekNum.toString().padStart(2, '0')}`;

      if (!weeks[weekKey]) {
        weeks[weekKey] = {
          week: weekKey,
          startDate: bill.trandate,
          count: 0,
          total: 0,
          paid: 0,
          open: 0,
          bills: []
        };
      }

      weeks[weekKey].count++;
      weeks[weekKey].total += bill.amount;
      if (bill.status === 'B') {
        weeks[weekKey].paid += bill.amount;
      } else if (bill.status === 'A') {
        weeks[weekKey].open += bill.amount;
      }
      weeks[weekKey].bills.push(bill);
    });

    Object.values(weeks).forEach(week => {
      week.bills.sort((a, b) => b.amount - a.amount);
    });

    return Object.values(weeks).sort((a, b) => a.week.localeCompare(b.week));
  }, [bills]);

  const totals = useMemo(() => {
    return bills.reduce((acc, bill) => {
      acc.count++;
      acc.total += bill.amount;
      if (bill.status === 'B') acc.paid += bill.amount;
      if (bill.status === 'A') acc.open += bill.amount;
      return acc;
    }, { count: 0, total: 0, paid: 0, open: 0 });
  }, [bills]);

  const top10Total = useMemo(() => {
    return top10Vendors.reduce((sum, v) => sum + v.ytdTotal, 0);
  }, [top10Vendors]);

  const statusDistribution = useMemo(() => {
    const dist = { A: 0, B: 0 };
    bills.forEach(b => {
      if (dist[b.status] !== undefined) {
        dist[b.status] += b.amount;
      }
    });
    return [
      { name: 'Open', value: dist.A },
      { name: 'Paid', value: dist.B }
    ];
  }, [bills]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const toggleWeek = (week) => {
    setExpandedWeek(expandedWeek === week ? null : week);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 w-full max-w-sm">
          <div className="flex flex-col items-center mb-6">
            <div className="p-3 bg-gray-100 rounded-full mb-3">
              <Lock className="w-6 h-6 text-gray-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Original Footwear Data Analytics</h1>
            <p className="text-sm text-gray-500 mt-1">Enter password to continue</p>
          </div>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setAuthError(false); }}
              placeholder="Password"
              className={`w-full px-3 py-2 rounded-lg border text-sm outline-none transition-colors ${
                authError ? 'border-red-300 focus:border-red-400' : 'border-gray-300 focus:border-gray-400'
              }`}
              autoFocus
            />
            {authError && <p className="text-red-500 text-xs mt-1.5">Incorrect password</p>}
            <button
              type="submit"
              className="w-full mt-4 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
            >
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          <p className="text-gray-600 font-medium">Loading data from NetSuite...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <img src="/original-footwear-logo.webp" alt="Original Footwear" className="h-10" />
              <span className="text-gray-400 font-light">/</span> Vendor Expenses
            </h1>
            <p className="text-gray-500 mt-1">Weekly Summary Dashboard</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right text-sm text-gray-500">
              <p>Last refreshed:</p>
              <p className="font-medium text-gray-700">{lastRefreshed.toLocaleString()}</p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all border ${
                isRefreshing
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-100 active:scale-95'
              }`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {fetchError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800">Failed to refresh data from NetSuite</p>
              <p className="text-sm text-red-600 mt-1">{fetchError}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Bills</p>
                <p className="text-xl font-bold text-gray-900">{totals.count}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Amount</p>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(totals.total)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Paid</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(totals.paid)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Calendar className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Open</p>
                <p className="text-xl font-bold text-red-600">{formatCurrency(totals.open)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('weekly')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'weekly'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            Weekly Summary
          </button>
          <button
            onClick={() => setActiveTab('vendors')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'vendors'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            Top 10 Vendors
          </button>
        </div>

        {activeTab === 'weekly' && (
          <>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Weekly Volume</h2>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={weeklySummary}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    {weeklySummary.map((week, index) =>
                      index % 2 === 1 ? (
                        <ReferenceArea key={week.week} x1={week.week} x2={week.week} fill="#f3f4f6" fillOpacity={0.6} />
                      ) : null
                    )}
                    <XAxis dataKey="week" tick={{ fontSize: 11, angle: -45, textAnchor: 'end' }} height={60} interval={0} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Legend />
                    <Bar dataKey="paid" name="Paid" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="open" name="Open" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Payment Status</h2>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={statusDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={85}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      <Cell fill="#ef4444" />
                      <Cell fill="#22c55e" />
                    </Pie>
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-800">Weekly Summary</h2>
                <p className="text-sm text-gray-500 mt-1">Click on any week to expand and view bill details (sorted by highest amount)</p>
              </div>
              <div className="overflow-hidden">
                {weeklySummary.map((week) => (
                  <div key={week.week} className="border-b border-gray-100 last:border-b-0">
                    <div
                      onClick={() => toggleWeek(week.week)}
                      className={`flex items-center px-4 py-3 cursor-pointer transition-colors ${
                        expandedWeek === week.week ? 'bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2 w-32">
                        {expandedWeek === week.week ? (
                          <ChevronDown className="w-5 h-5 text-blue-600" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                        <span className={`font-semibold ${expandedWeek === week.week ? 'text-blue-600' : 'text-gray-900'}`}>
                          {week.week}
                        </span>
                      </div>
                      <div className="flex-1 grid grid-cols-5 gap-4 text-sm">
                        <div className="text-center">
                          <span className="text-gray-500">Bills:</span>
                          <span className="ml-1 font-medium text-gray-900">{week.count}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-gray-500">Total:</span>
                          <span className="ml-1 font-semibold text-gray-900">{formatCurrency(week.total)}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-gray-500">Paid:</span>
                          <span className="ml-1 font-medium text-green-600">{formatCurrency(week.paid)}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-gray-500">Open:</span>
                          <span className="ml-1 font-medium text-red-600">{formatCurrency(week.open)}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-gray-500">% Paid:</span>
                          <span className="ml-1 font-medium text-gray-700">
                            {week.total > 0 ? `${((week.paid / week.total) * 100).toFixed(0)}%` : '0%'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {expandedWeek === week.week && (
                      <div className="bg-gray-50 border-t border-gray-200 overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-12">#</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bill #</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">G/L Account</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {week.bills.map((bill, index) => (
                              <tr key={bill.id} className="bg-white hover:bg-gray-100">
                                <td className="px-4 py-2 text-sm text-gray-500">
                                  <span className="text-gray-400">{index + 1}</span>
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">{bill.trandate}</td>
                                <td className="px-4 py-2 text-sm font-medium text-gray-900">{bill.tranid}</td>
                                <td className="px-4 py-2 text-sm text-gray-600">{bill.vendor}</td>
                                <td className="px-4 py-2 text-sm text-gray-600">
                                  <span className="inline-flex px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded border border-blue-200">
                                    {bill.glAccount || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-right">
                                  <span className="font-semibold text-gray-900 text-sm">
                                    {formatCurrency(bill.amount)}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-center">
                                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                    bill.status === 'B' ? 'bg-green-100 text-green-700' :
                                    bill.status === 'A' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                                  }`}>
                                    {statusLabels[bill.status]?.label || bill.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-100">
                            <tr>
                              <td colSpan={5} className="px-4 py-2 text-sm font-semibold text-gray-700 text-right">
                                Week {week.week} Total:
                              </td>
                              <td className="px-4 py-2 text-right font-bold text-gray-900">{formatCurrency(week.total)}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="bg-gray-800 text-white px-4 py-3 flex items-center">
                <div className="w-32 font-bold">TOTAL</div>
                <div className="flex-1 grid grid-cols-5 gap-4 text-sm">
                  <div className="text-center font-semibold">{totals.count} bills</div>
                  <div className="text-right font-bold">{formatCurrency(totals.total)}</div>
                  <div className="text-right text-green-400 font-semibold">{formatCurrency(totals.paid)}</div>
                  <div className="text-right text-red-400 font-semibold">{formatCurrency(totals.open)}</div>
                  <div className="text-right font-semibold">{totals.total > 0 ? ((totals.paid / totals.total) * 100).toFixed(0) : 0}%</div>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'vendors' && (
          <>
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Top 10 Vendors - {new Date().getFullYear()}</h2>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={top10Vendors}
                  layout="vertical"
                  margin={{ left: 200, right: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 13 }} width={190} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Bar dataKey="ytdTotal" name="YTD Amount" fill="#dbeafe" stroke="#2563eb" strokeWidth={2} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-semibold text-gray-800">Top 10 Vendors - {new Date().getFullYear()}</h2>
                </div>
                <div className="text-sm text-gray-500">
                  Total: <span className="font-semibold text-gray-900">{formatCurrency(top10Total)}</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-16">Rank</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor Name</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">ID</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Bills</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">YTD Amount</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% of Top 10</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-48">Distribution</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {top10Vendors.map((vendor, index) => (
                      <tr key={vendor.vendorId} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                            index === 0 ? 'bg-yellow-100 text-yellow-700' :
                            index === 1 ? 'bg-gray-200 text-gray-700' :
                            index === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {vendor.rank}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 text-base">{vendor.name}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{vendor.entityId}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 text-right">{vendor.billCount}</td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-semibold text-gray-900">{formatCurrency(vendor.ytdTotal)}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 text-right">
                          {((vendor.ytdTotal / top10Total) * 100).toFixed(1)}%
                        </td>
                        <td className="px-4 py-3">
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className="h-3 rounded-full transition-all duration-500 border-2 border-blue-600"
                              style={{
                                width: `${top10Vendors[0]?.ytdTotal ? (vendor.ytdTotal / top10Vendors[0].ytdTotal) * 100 : 0}%`,
                                backgroundColor: '#dbeafe'
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-100">
                    <tr>
                      <td className="px-4 py-3 text-center font-bold text-gray-900" colSpan={3}>TOTAL (Top 10)</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">
                        {top10Vendors.reduce((sum, v) => sum + v.billCount, 0)}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">{formatCurrency(top10Total)}</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">100%</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        )}

        <div className="mt-6 text-center text-sm text-gray-500">
          Data source: NetSuite • Generated: {new Date().toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}

export default App;
